import {create} from 'zustand';
import type {Sale} from '../models/sale.model';
import type {SalesStatistics} from '../models/sale.model';
import type {BaseResponse} from '../../../Data/types/BaseResponse';

interface SalesState {
  sales: Sale[];
  statistics: SalesStatistics | null;
  loading: boolean;
  error: string | null;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };

  // Actions
  fetchSales: (page?: number, limit?: number, filters?: { search?: string; marketerName?: string; marketerId?: string; invoiceNumber?: string }) => Promise<void>;
  fetchStatistics: () => Promise<void>;
  fetchSaleById: (id: string) => Promise<Sale | null>;
  fetchSaleByInvoiceNumber: (invoiceNumber: string) => Promise<Sale | null>;
  updateSale: (id: string, saleData: Partial<Sale>) => Promise<void>;
  deleteSale: (id: string) => Promise<void>;
  clearError: () => void;
  setPagination: (page: number, limit: number, total: number) => void;
}

export const useSalesStore = create<SalesState>((set, get) => ({
  sales: [],
  statistics: null,
  loading: false,
  error: null,
  pagination: {
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 1
  },

  fetchSales: async (page = 1, limit = 20, filters?: { search?: string; marketerName?: string; marketerId?: string; invoiceNumber?: string }) => {
    set({loading: true, error: null});

    try {
      // This will be injected by the factory
      const getSalesUseCase = (get() as any).getSalesUseCase;
      if (!getSalesUseCase) {
        throw new Error('GetSalesUseCase not initialized');
      }

      const response = await getSalesUseCase.execute(page, limit, filters);

      set({
        sales: response.items,
        loading: false,
        pagination: {
          page,
          limit,
          total: response.pagination?.total || response.items.length,
          totalPages: response.pagination?.totalPages || Math.ceil(response.items.length / limit)
        }
      });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'حدث خطأ غير متوقع',
        loading: false
      });
    }
  },

  fetchStatistics: async () => {
    set({loading: true, error: null});

    try {
      const getStatisticsUseCase = (get() as any).getStatisticsUseCase;
      if (!getStatisticsUseCase) {
        throw new Error('GetSalesStatisticsUseCase not initialized');
      }

      const response: BaseResponse<SalesStatistics> = await getStatisticsUseCase.execute();

      if (response.stat_code === 200) {
        set({
          statistics: response.data,
          loading: false
        });
      } else {
        set({
          error: response.stat_message || 'حدث خطأ في جلب إحصائيات المبيعات',
          loading: false
        });
      }
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'حدث خطأ غير متوقع',
        loading: false
      });
    }
  },

  fetchSaleById: async (id: string) => {
    set({loading: true, error: null});

    try {
      const getSaleByIdUseCase = (get() as any).getSaleByIdUseCase;
      if (!getSaleByIdUseCase) {
        throw new Error('GetSaleByIdUseCase not initialized');
      }

      const response: BaseResponse<Sale> = await getSaleByIdUseCase.execute(id);

      if (response.stat_code === 200) {
        set({loading: false});
        return response.data;
      } else {
        set({
          error: response.stat_message || 'حدث خطأ في جلب المبيعات',
          loading: false
        });
        return null;
      }
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'حدث خطأ غير متوقع',
        loading: false
      });
      return null;
    }
  },

  fetchSaleByInvoiceNumber: async (invoiceNumber: string) => {
    set({loading: true, error: null});

    try {
      const getSaleByInvoiceNumberUseCase = (get() as any).getSaleByInvoiceNumberUseCase;
      if (!getSaleByInvoiceNumberUseCase) {
        throw new Error('GetSaleByInvoiceNumberUseCase not initialized');
      }

      const response: BaseResponse<Sale> = await getSaleByInvoiceNumberUseCase.execute(invoiceNumber);

      if (response.stat_code === 200) {
        set({loading: false});
        return response.data;
      } else {
        set({
          error: response.stat_message || 'حدث خطأ في جلب تفاصيل الفاتورة',
          loading: false
        });
        return null;
      }
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'حدث خطأ غير متوقع',
        loading: false
      });
      return null;
    }
  },

  updateSale: async (id: string, saleData: Partial<Sale>) => {
    set({loading: true, error: null});

    try {
      const updateSaleUseCase = (get() as any).updateSaleUseCase;
      if (!updateSaleUseCase) {
        throw new Error('UpdateSaleUseCase not initialized');
      }

      const response: BaseResponse<Sale> = await updateSaleUseCase.execute(id, saleData);

      if (response.stat_code === 200) {
        // Update the sale in the list
        const currentSales = get().sales;
        const updatedSales = currentSales.map(sale =>
          sale.id === id ? response.data : sale
        );

        set({
          sales: updatedSales,
          loading: false
        });
      } else {
        set({
          error: response.stat_message || 'حدث خطأ في تحديث المبيعات',
          loading: false
        });
      }
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'حدث خطأ غير متوقع',
        loading: false
      });
    }
  },

  deleteSale: async (id: string) => {
    set({loading: true, error: null});

    try {
      const deleteSaleUseCase = (get() as any).deleteSaleUseCase;
      if (!deleteSaleUseCase) {
        throw new Error('DeleteSaleUseCase not initialized');
      }

      const response: BaseResponse<void> = await deleteSaleUseCase.execute(id);

      if (response.stat_code === 200) {
        // Remove the sale from the list
        const currentSales = get().sales;
        const filteredSales = currentSales.filter(sale => sale.id !== id);

        set({
          sales: filteredSales,
          loading: false
        });
      } else {
        set({
          error: response.stat_message || 'حدث خطأ في حذف المبيعات',
          loading: false
        });
      }
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'حدث خطأ غير متوقع',
        loading: false
      });
    }
  },

  clearError: () => {
    set({error: null});
  },

  setPagination: (page: number, limit: number, total: number) => {
    set({
      pagination: {
        page,
        limit,
        total,
        totalPages: Math.ceil(total / limit)
      }
    });
  }
}));
